// src/components/AnnouncementDetailPage.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { CalendarDays, UserCircle, ArrowLeft, Share2 } from 'lucide-react';
import { Badge } from './ui/badge.tsx';
import { Card, CardContent } from './ui/card.tsx';
import { Button } from './ui/button.tsx';
import { motion } from 'framer-motion';

interface AnnouncementAuthor {
    id: number;
    FirstName: string;
    LastName: string;
    profile?: { ProfilePicture?: string, Occupation?: string };
}

interface Announcement {
    id: number;
    Title: string;
    Content: string;
    Category: string;
    PublishDate: string;
    Image?: string | null;
    author?: AnnouncementAuthor;
}

export function AnnouncementDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [announcement, setAnnouncement] = useState<Announcement | null>(null);
    const [loading, setLoading] = useState(true);

    const getFullImageUrl = (urlPath: string | null | undefined) => {
        if (!urlPath) return undefined;
        if (urlPath.startsWith('http')) return urlPath;
        const baseUrl = api.defaults.baseURL?.replace('/api', '') || 'https://sdal-project-backend.onrender.com';
        return `${baseUrl}${urlPath}`;
    };

    useEffect(() => {
        const fetchDetail = async () => {
            try {
                const response = await api.get(`/announcements/${id}/`);
                setAnnouncement(response.data);
            } catch (error) {
                console.error("Duyuru bulunamadı", error);
            } finally {
                setLoading(false);
            }
        };
        fetchDetail();
    }, [id]);

    if (loading) return <div className="text-center py-20 text-slate-400 animate-pulse">İçerik yükleniyor...</div>;
    if (!announcement) return <div className="text-center py-20">Duyuru bulunamadı.</div>;

    return (
        <div className="min-h-screen bg-slate-50/50 pb-20">
            <div className="container mx-auto px-4 pt-8 max-w-5xl">
                
                {/* Geri Dön Butonu */}
                <Button 
                    variant="ghost" 
                    className="mb-6 gap-2 text-slate-500 hover:text-[#A8322D]"
                    onClick={() => navigate('/duyurular')}
                >
                    <ArrowLeft className="h-4 w-4" /> Duyurulara Dön
                </Button>

                <div className="grid lg:grid-cols-3 gap-10 items-start">
                    
                    {/* SOL TARAF: ANA İÇERİK */}
                    <div className="lg:col-span-2 space-y-6">
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            className="bg-white rounded-3xl p-6 md:p-10 shadow-sm border border-slate-100"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <Badge className="bg-[#A8322D]/10 text-[#A8322D] border-none px-4 py-1">
                                    {announcement.Category.replace('_', ' ')}
                                </Badge>
                                <span className="text-slate-400 text-sm flex items-center gap-1">
                                    <CalendarDays className="h-4 w-4" />
                                    {new Date(announcement.PublishDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                </span>
                            </div>

                            <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-8 leading-tight">
                                {announcement.Title}
                            </h1>

                            {announcement.Image && (
                                <div className="rounded-2xl overflow-hidden mb-10 shadow-lg">
                                    <img src={getFullImageUrl(announcement.Image)} alt="" className="w-full h-auto" />
                                </div>
                            )}

                            <div className="prose prose-slate max-w-none text-slate-700 leading-loose text-lg">
                                {announcement.Content.split('\n').map((p, i) => (
                                    <p key={i} className="mb-4">{p}</p>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    {/* SAĞ TARAF: YAZAR KARTI (TAMAMI TIKLANABİLİR) */}
                    <aside className="space-y-6 sticky top-28">
                        {announcement.author && (
                            <motion.div 
                                initial={{ opacity: 0, x: 20 }} 
                                animate={{ opacity: 1, x: 0 }} 
                                transition={{ delay: 0.2 }}
                                onClick={() => navigate(`/profil?id=${announcement.author?.id}`)}
                                className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 text-center cursor-pointer hover:bg-slate-50 hover:border-slate-200 transition-all group duration-300"
                            >
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 group-hover:text-[#A8322D] transition-colors">Paylaşan Kişi</p>
                                <div className="relative w-20 h-20 mx-auto mb-4">
                                    <div className="w-full h-full rounded-full overflow-hidden border-2 border-[#A8322D]/20 p-1 group-hover:border-[#A8322D]/40 transition-colors">
                                        <div className="w-full h-full rounded-full overflow-hidden bg-slate-100">
                                            {announcement.author.profile?.ProfilePicture ? (
                                                <img src={getFullImageUrl(announcement.author.profile.ProfilePicture)} className="w-full h-full object-cover" alt="" />
                                            ) : <UserCircle className="w-full h-full text-slate-300" />}
                                        </div>
                                    </div>
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-[#A8322D] transition-colors">
                                    {announcement.author.FirstName} {announcement.author.LastName}
                                </h3>
                                <p className="text-sm text-slate-500 mb-6 italic">
                                    {announcement.author.profile?.Occupation || "SDAL Mezunu"}
                                </p>
                                
                                <div className="text-xs font-semibold text-slate-400 pt-3 border-t border-slate-50 group-hover:text-blue-600 transition-colors">
                                    Profili incelemek için tıklayın &rarr;
                                </div>
                            </motion.div>
                        )}

                        <div className="flex gap-2">
                             <Button variant="outline" className="w-full rounded-xl gap-2 text-slate-600 border-slate-200 hover:bg-slate-50" onClick={() => window.print()}>
                                <Share2 className="h-4 w-4" /> Sayfayı Yazdır
                             </Button>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}